import React, { useState } from 'react';
import { collection, addDoc, Timestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaFileUpload, FaFile, FaTrash } from 'react-icons/fa';
import { createNotification, NotificationType } from '../../services/NotificationService';

const RequestForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('normal');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [success, setSuccess] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [sending, setSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024; // 5MB

    // Dosya boyutu kontrolü
    const validFiles = selectedFiles.filter(file => file.size <= maxSize);
    if (validFiles.length !== selectedFiles.length) {
      setError('Bazı dosyalar 5MB limitini aşıyor ve eklenemedi.');
      setTimeout(() => setError(null), 3000);
    }

    // Dosyaları base64'e çevir
    const processedFiles = await Promise.all(
      validFiles.map(async (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              name: file.name,
              type: file.type,
              size: file.size,
              base64: reader.result
            });
          };
          reader.onerror = (error) => {
            console.error('Dosya okuma hatası:', error);
            reject(error);
          };
          reader.readAsDataURL(file);
        });
      })
    );

    setFiles(prevFiles => [...prevFiles, ...processedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim() || !category) {
      setError('Lütfen gerekli alanları doldurun.');
      return;
    }

    try {
      setSending(true);
      setLoading(true);
      setError(null);

      // Dosyaları doğrudan base64 formatında kullan
      const uploadedFiles = files.map(file => ({
        name: file.name,
        type: file.type,
        size: file.size,
        base64: file.base64
      }));

      // İstek verilerini hazırla
      const requestData = {
        title: title.trim(),
        description: description.trim(),
        category,
        priority,
        userId: user.uid,
        userName: user.displayName,
        userEmail: user.email,
        status: 'pending',
        files: uploadedFiles,
        createdAt: Timestamp.fromDate(new Date()),
        type: 'special',
        lastUpdated: Timestamp.fromDate(new Date())
      };

      // İsteği kaydet
      const docRef = await addDoc(collection(db, 'requests'), requestData);

      // Admin ve editörlere bildirim gönder
      const usersRef = collection(db, 'users');
      const adminQuery = query(usersRef, where('role', 'in', ['admin', 'editor']));
      const adminSnapshot = await getDocs(adminQuery);
      
      adminSnapshot.forEach(async (doc) => {
        await createNotification(user.uid, NotificationType.REQUEST_CREATED, {
          requestId: docRef.id,
          requestTitle: title,
          senderName: user.displayName || user.email,
          priority,
          category
        });
      });

      // Başarı durumunu güncelle
      setSuccess(true);
      setShowSuccess(true);

      // Formu temizle
      setTitle('');
      setDescription('');
      setCategory('');
      setPriority('normal');
      setFiles([]);

      // 3 saniye sonra requests sayfasına yönlendir
      setTimeout(() => {
        navigate('/requests', { 
          state: { 
            showSuccess: true,
            message: 'Özel isteğiniz başarıyla oluşturuldu ve kaydedildi.' 
          }
        });
      }, 3000);

    } catch (error) {
      console.error('İstek oluşturulurken hata:', error);
      setError(error.message || 'İstek oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSending(false);
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Başlık ve Bilgilendirme */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Özel İstek Oluştur</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          İhtiyaçlarınıza özel çözümler için formu doldurun. Ekibimiz en kısa sürede sizinle iletişime geçecektir.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Sol Taraf - Bilgilendirme Kartları */}
        <div className="space-y-4">
          {/* Süreç Bilgilendirme */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 shadow-sm border border-blue-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Süreç Nasıl İşliyor?</h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                  1
                </div>
                <div className="ml-3">
                  <p className="text-sm text-gray-600">İsteğinizi detaylı bir şekilde açıklayın</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                  2
                </div>
                <div className="ml-3">
                  <p className="text-sm text-gray-600">Ekibimiz isteğinizi inceleyip değerlendirecek</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                  3
                </div>
                <div className="ml-3">
                  <p className="text-sm text-gray-600">Size özel fiyat teklifi sunulacak</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                  4
                </div>
                <div className="ml-3">
                  <p className="text-sm text-gray-600">Onayınız sonrası geliştirme başlayacak</p>
                </div>
              </div>
            </div>
          </div>

          {/* İpuçları */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 shadow-sm border border-green-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">İpuçları</h3>
            <ul className="space-y-2">
              <li className="flex items-center text-sm text-gray-600">
                <FaFileUpload className="text-green-500 mr-2 flex-shrink-0" />
                İsteğinizi mümkün olduğunca detaylı açıklayın
              </li>
              <li className="flex items-center text-sm text-gray-600">
                <FaFile className="text-green-500 mr-2 flex-shrink-0" />
                Varsa örnek dosyaları ekleyin
              </li>
              <li className="flex items-center text-sm text-gray-600">
                <FaFileUpload className="text-green-500 mr-2 flex-shrink-0" />
                Beklentilerinizi net bir şekilde belirtin
              </li>
              <li className="flex items-center text-sm text-gray-600">
                <FaFile className="text-green-500 mr-2 flex-shrink-0" />
                Zaman kısıtlaması varsa belirtmeyi unutmayın
              </li>
            </ul>
          </div>
        </div>

        {/* Sağ Taraf - Form */}
        <div className="md:col-span-2">
          <div className="bg-white rounded-xl shadow-lg p-6">
            {success ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaFileUpload className="text-green-500 text-3xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">İsteğiniz Başarıyla Oluşturuldu!</h3>
                <p className="text-gray-600 mb-4">Ekibimiz en kısa sürede sizinle iletişime geçecektir.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-md">
                    <div className="flex">
                      <FaFileUpload className="text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="ml-3">
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Başlık <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="İsteğiniz için kısa ve açıklayıcı bir başlık"
                  />
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    Kategori <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Seçiniz</option>
                    <option value="automation">Otomasyon</option>
                    <option value="excel">Excel VBA</option>
                    <option value="web">Web Uygulaması</option>
                    <option value="desktop">Masaüstü Uygulama</option>
                    <option value="other">Diğer</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                    Öncelik
                  </label>
                  <select
                    id="priority"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Düşük</option>
                    <option value="normal">Normal</option>
                    <option value="high">Yüksek</option>
                    <option value="urgent">Acil</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Açıklama <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="İsteğinizi detaylı bir şekilde açıklayın..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dosyalar
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-500 transition-colors">
                    <div className="space-y-1 text-center">
                      <FaFileUpload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                          <span>Dosya Yükle</span>
                          <input
                            id="file-upload"
                            type="file"
                            className="sr-only"
                            multiple
                            onChange={handleFileChange}
                          />
                        </label>
                        <p className="pl-1">veya sürükleyip bırakın</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        Her dosya en fazla 5MB olabilir
                      </p>
                    </div>
                  </div>

                  {files.length > 0 && (
                    <ul className="mt-4 space-y-2">
                      {files.map((file, index) => (
                        <li key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <FaFile className="text-gray-400 mr-2" />
                            <span className="text-sm text-gray-600">{file.name}</span>
                            <span className="text-xs text-gray-500 ml-2">
                              ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700 transition-colors"
                          >
                            <FaTrash />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all flex items-center ${
                      loading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                        Gönderiliyor...
                      </>
                    ) : (
                      'İsteği Gönder'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      {/* Başarı Mesajı */}
      {showSuccess && (
        <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-6 py-4 rounded-lg z-50 shadow-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FaFileUpload className="h-6 w-6 text-green-500" />
            </div>
            <div className="ml-3">
              <p className="font-medium">İşlem Başarılı!</p>
              <p className="text-sm mt-1">Özel isteğiniz başarıyla oluşturuldu. Yönlendiriliyorsunuz...</p>
            </div>
          </div>
        </div>
      )}

      {/* Hata Mesajı */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg z-50 shadow-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FaFileUpload className="h-6 w-6 text-red-500" />
            </div>
            <div className="ml-3">
              <p className="font-medium">Hata!</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestForm; 